/**
 * @file ChartsPreview.tsx
 * @brief DEV ONLY: TrendChart loading next to loaded, to check the skeleton
 *        matches the chart. Mounted at `/__preview/charts` when DEV.
 *
 * @author DES
 * @date 03-10-2026
 */

import { useState } from "react";
import { TrendChart } from "../components/TrendChart";
import { formatMoneyMinor } from "@/shared/utils/formatters";
import { ProductLabelCell } from "@/features/reports-shared/productIcons";
import { LanguagePairCell } from "@/features/reports-shared/LanguagePairCell";
import { Avatar } from "@/components/ui/Avatar";
import { StatCard } from "../components/StatCard";
import { Activity, CreditCard, UsersRound } from "lucide-react";

const DATA = Array.from({ length: 14 }, (_, i) => ({
  period: `2026-09-${String(i + 10).padStart(2, "0")}`,
  sessions: [3, 5, 4, 7, 6, 9, 8, 11, 9, 12, 10, 14, 13, 15][i],
  revenue: [400, 520, 480, 700, 650, 900, 820, 1100, 950, 1250, 1100, 1400, 1300, 1550][i],
  cost: [150, 210, 180, 260, 240, 330, 300, 400, 350, 450, 400, 500, 480, 560][i],
}));

const ONE = [{ dataKey: "sessions", label: "Sessions", color: "hsl(var(--primary))" }];
const TWO = [
  { dataKey: "revenue", label: "Revenue", color: "hsl(var(--success))", format: (v: number) => formatMoneyMinor(v, "GBP") },
  { dataKey: "cost", label: "Cost", color: "hsl(var(--warning))", format: (v: number) => formatMoneyMinor(v, "GBP") },
];

export function ChartsPreviewPage() {
  const [loading, setLoading] = useState(true);
  return (
    <div className="space-y-6 p-6">
      <button
        type="button"
        className="rounded-full bg-secondary px-4 py-1.5 text-sm"
        onClick={() => setLoading((v) => !v)}
      >
        {loading ? "Show loaded" : "Show loading"}
      </button>
      {/* Company dashboard KPI cells (03-10-2026): delta pill + link. */}
      <div className="grid grid-cols-1 sm:grid-cols-3">
        <StatCard variant="cell" label="Used Verblizr this month" icon={UsersRound} tone="brand" value="2 of 3" hint="3 active employees" to="#" />
        <StatCard variant="cell" label="Sessions this month" icon={Activity} tone="success" value="7" hint="vs the same days last month" delta={40} to="#" />
        <StatCard variant="cell" label="Spend this month" icon={CreditCard} tone="warning" value="£14.00" hint="£2.00 outstanding" delta={-12} to="#" />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <TrendChart variant="cell" title="Sessions" subtitle="Per day" data={DATA} series={ONE} isLoading={loading} height={240} />
        <TrendChart variant="cell" title="Revenue & cost" subtitle="In GBP, per day" data={DATA} series={TWO} isLoading={loading} height={240} />
      </div>
      {/* Report cells (03-10-2026): product icons, flag pairs, avatars. */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-3">
          {[["conversation", "Interpretation"], ["note", "Personal Notes"], ["boardroom", "Meeting Intel"], ["subscription", "Plan seats"]].map(([k, l]) => (
            <div key={k}><ProductLabelCell product={k} label={l} /></div>
          ))}
        </div>
        <div className="space-y-3">
          {[["en", "en"], ["en-GB", "ur-PK"], ["ur", "en"], ["en", "sq"]].map(([a, b]) => (
            <div key={a + b}><LanguagePairCell source={a} target={b} /></div>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Avatar initials="AK" size="md" />
          <div><div className="font-medium">Ahmed Khan</div><div className="text-xs text-muted-foreground">initials fallback</div></div>
        </div>
      </div>
    </div>
  );
}
